import React, { useEffect, useMemo, useRef, useState } from 'react';
import { t } from '@bay-email/core';
import { BlockAvatarWrapper, useEditorProps } from '@bay-email/editor';
import BaySelect from '@bay-ui/BaySelect';
import BaySearchInput from '@bay-ui/BaySearchInput';
import BaySpinner from '@bay-ui/BaySpinner';
import { BsGripVertical } from 'react-icons/bs';
import styles from './index.module.scss';

// Built per render, not at module scope: t() resolves against the locale that is
// loaded when it runs, and a module-level call freezes whatever was in place at
// import time. Same reason the block categories use `get label()`.
const allCategoriesOption = () => ({ label: t('All Categories'), value: '' });
const allCompaniesOption = () => ({ label: t('All Companies'), value: '' });

/**
 * Browser for saved reusable blocks, shown under the Content panel's "Block
 * Library" tab.
 *
 * Presentational only. The filter/search state lives here because it is UI
 * state, but the query is handed straight back to the host through
 * `blockLibrary.onQueryChange` — the editor has no idea where blocks are stored
 * or who is allowed to see them, and must not learn.
 */
export function BlockLibrary() {
	const { blockLibrary } = useEditorProps();
	const {
		categoryOptions = [],
		companyOptions = [],
		items = [],
		loading = false,
		onQueryChange,
	} = blockLibrary || {};

	const [search, setSearch] = useState('');
	const [category, setCategory] = useState('');
	const [company, setCompany] = useState('');

	// One notification per settled query. The host decides whether that means a
	// refetch or a filter over what it already holds.
	//
	// Deliberately keyed on the query values ALONE. Holding the callback in a ref
	// means a host that passes an inline arrow — the normal thing to write — gets
	// one call per query change instead of one per render, which with a fetch on
	// the other end is an infinite loop.
	const onQueryChangeRef = useRef(onQueryChange);
	onQueryChangeRef.current = onQueryChange;

	useEffect(() => {
		onQueryChangeRef.current?.({ search, category, company });
	}, [search, category, company]);

	const categorySelectOptions = useMemo(
		() => [allCategoriesOption(), ...categoryOptions],
		[categoryOptions],
	);
	const companySelectOptions = useMemo(
		() => [allCompaniesOption(), ...companyOptions],
		[companyOptions],
	);

	const selectedCategory =
		categorySelectOptions.find(option => option.value === category) ||
		categorySelectOptions[0];
	const selectedCompany =
		companySelectOptions.find(option => option.value === company) ||
		companySelectOptions[0];

	// Blocks arrive flat and are grouped for display only: the category is a
	// property of the block, not a level the host has to pre-nest.
	const groups = useMemo(() => {
		const byCategory = new Map<string, typeof items>();

		items.forEach(item => {
			const key = item.categoryLabel || t('Uncategorized');
			byCategory.set(key, [...(byCategory.get(key) || []), item]);
		});

		return [...byCategory.entries()].sort(([a], [b]) => a.localeCompare(b));
	}, [items]);

	return (
		<div className={styles.blockLibrary}>
			<div className={styles.blockLibraryFilters}>
				<BaySelect
					optionsArray={categorySelectOptions}
					controlledValue={selectedCategory}
					onChangeFunc={(option: any) => setCategory(option?.value ?? '')}
					isClearable={false}
					allowSearch={false}
					menuPortalTarget={document.body}
					menuPosition='fixed'
				/>
				<BaySelect
					optionsArray={companySelectOptions}
					controlledValue={selectedCompany}
					onChangeFunc={(option: any) => setCompany(option?.value ?? '')}
					isClearable={false}
					allowSearch={false}
					menuPortalTarget={document.body}
					menuPosition='fixed'
				/>
				<BaySearchInput
					className='m-0'
					showClear
					placeholder={t('Search block library...')}
					onSearch={(value: string) => setSearch(value || '')}
					onChange={(value: string) => {
						if (!value) setSearch('');
					}}
				/>
			</div>

			<hr className={styles.blockLibraryDivider} />

			{loading ? (
				<div className={styles.blockLibraryState}>
					<BaySpinner />
				</div>
			) : groups.length === 0 ? (
				<div className={styles.blockLibraryState}>
					{search || category || company
						? t('No blocks match these filters.')
						: t('No saved blocks yet.')}
				</div>
			) : (
				groups.map(([groupLabel, groupItems]) => (
					<div key={groupLabel}>
						<h2 className='fs-5 p-3 m-0'>{groupLabel}</h2>
						<div className='px-3 pb-2'>
							{groupItems.map(item => (
								<BlockLibraryItem
									key={item.id}
									item={item}
								/>
							))}
						</div>
					</div>
				))
			)}
		</div>
	);
}

type BlockLibraryItemProps = {
	item: NonNullable<
		NonNullable<ReturnType<typeof useEditorProps>['blockLibrary']>['items']
	>[number];
};

/**
 * One saved block, dragged into the canvas by the same machinery as a built-in
 * block: BlockAvatarWrapper puts { type, payload } on the drag and the drop
 * target runs it through the block factory. The payload is the block's stored
 * Wrapper subtree, so what lands on the canvas is a normal, fully editable
 * wrapper — not a reference to the saved block. Editing it afterwards changes
 * this email only.
 */
function BlockLibraryItem({ item }: BlockLibraryItemProps) {
	const card = (
		<div className={styles.blockLibraryCard}>
			<div className={styles.blockLibraryPreview}>
				{item.thumbnailUrl ? (
					<img
						className={styles.blockLibraryThumb}
						src={item.thumbnailUrl}
						alt={item.name}
						loading='lazy'
						// Without this the browser drags the image itself, so the drop
						// carries a bare <img> instead of the block.
						draggable={false}
					/>
				) : (
					<div className={styles.blockLibraryThumbEmpty} />
				)}
			</div>
			<div className={styles.blockLibraryItemFooter}>
				{/* Decorative: the whole card is the drag source, so the grip is a
				    hint rather than a handle. Hidden from screen readers, which get
				    the draggable card itself. */}
				<BsGripVertical
					className={styles.blockLibraryGrip}
					aria-hidden='true'
				/>
				<span className={styles.blockLibraryItemName}>{item.name}</span>
			</div>
		</div>
	);

	if (!item.content?.type) {
		return (
			<div
				className={styles.blockLibraryItemWrapper}
				title={t('This block has no saved content to place.')}
			>
				{card}
			</div>
		);
	}

	return (
		<div className={styles.blockLibraryItemWrapper}>
			<BlockAvatarWrapper
				// A saved block's content is the whole Page holding its root
				// wrapper(s). Drag AS a wrapper so dragover accepts the drop at the
				// email root (a Page has no valid parent); the Page rides along as the
				// payload and useBlock.addBlock fans its wrappers out on drop.
				type={
					item.content.type === 'page'
						? item.content.children?.[0]?.type ?? item.content.type
						: item.content.type
				}
				payload={item.content}
			>
				{card}
			</BlockAvatarWrapper>
		</div>
	);
}
